import React, { useState } from 'react';

// PremiumLineChart — hand-rolled SVG line chart shared between the Inventory
// Trends drawer and the Price Tracker volume drawer. Theme-aware via CSS
// variables — no Chart.js dep, no canvas color-rehydration fight.
//
// Handles 1 point (dot only), 2 points (straight line + area), 3+ (cardinal
// spline). Period divider drawn at range.start; prior-period dots fade in
// before the divider.
//
// Why a single shared component (vs the previous inline copies):
//   - Operators see the same chart shape on Inventory Trends drawer and
//     Price Tracker volume drawer — visual asymmetry was confusing.
//   - Future polish (gridlines, hover treatment, axis formatting) lifts
//     both surfaces in one place.
//
// See DESIGN_SYSTEM.md §Inventory detail drawer (chart spec).

// Utility — share with consumers that need to render dates the same way as
// the chart's axis labels.
export function fmtDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Cardinal-spline (Catmull-Rom-style) path generator. Tension 0.35 = mild
// curvature, won't overshoot. Falls back to straight segments for n=2 so
// sparse data doesn't break the math.
export function cardinalSplinePath(pts, tension = 0.35) {
  if (pts.length < 2) return '';
  if (pts.length === 2) return `M${pts[0][0]},${pts[0][1]} L${pts[1][0]},${pts[1][1]}`;
  let d = `M${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) * tension / 3;
    const cp1y = p1[1] + (p2[1] - p0[1]) * tension / 3;
    const cp2x = p2[0] - (p3[0] - p1[0]) * tension / 3;
    const cp2y = p2[1] - (p3[1] - p1[1]) * tension / 3;
    d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return d;
}

/**
 * PremiumLineChart
 *
 * Props:
 *   series: Array<{ date: 'YYYY-MM-DD', value: number }>
 *   range: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' } — current period
 *   priorRange: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' } — prior period
 *           (chart spans [priorRange.start, range.end] combined; the dashed
 *            divider sits at range.start)
 *   yFormat: (value) => string — axis tick + tooltip number formatter
 *   gradientId?: string — DOM id of the linear gradient. Defaults to a
 *           reasonable shared id; pass a unique value if multiple charts
 *           render simultaneously on the same surface.
 *   highlightDate?: 'YYYY-MM-DD' — optional date whose data point should
 *           be visually emphasized: larger ringed dot + pinned label
 *           ("Current invoice"). Used by the Invoice Review drill-in to
 *           orient the operator on which delivery they're reviewing.
 *   highlightLabel?: string — text shown above the highlighted dot.
 *           Defaults to "Current invoice" when highlightDate is set.
 *   markers?: Array<{ date, label, tone? }> — pinned annotations at
 *           specific data points. tone ∈ 'amber' | 'critical' | 'info'.
 *           Used to call out pack changes ("50# → 45#") inline on the
 *           trend so the operator sees the cause-and-effect together.
 *   tooltipFormat?: (point) => Array<string> — optional multi-line
 *           tooltip override. Receives the full series point (so callers
 *           can stuff extras like `qty`, `lbs`, `packNote`, `estimated`
 *           onto each point and emit lines like ["Apr 7", "12 cases",
 *           "120 lb · 20×8 oz pack"]). When omitted, the tooltip falls
 *           back to a single line of yFormat(value) under the date —
 *           identical to the legacy behavior. Tooltip width and height
 *           grow with the line count + longest line so callers don't
 *           have to size it manually.
 */
export default function PremiumLineChart({
  series, range, priorRange, yFormat,
  gradientId = 'premium-chart-fill',
  highlightDate = null,
  highlightLabel = 'Current invoice',
  markers = [],
  tooltipFormat = null,
}) {
  const W = 480, H = 160;
  const padL = 44, padR = 12, padT = 12, padB = 4;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const [hover, setHover] = useState(null);

  const data = (series || []).filter(p => p.value != null && isFinite(p.value));

  if (data.length === 0) {
    return (
      <div
        className="rounded-lg flex items-center justify-center"
        style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', height: H, color: 'var(--text-muted)', fontSize: 13 }}
      >
        No data to plot for this metric.
      </div>
    );
  }

  // X scaling — date proportional over [priorRange.start, range.end].
  const startMs = new Date(priorRange.start + 'T00:00:00').getTime();
  const endMs = new Date(range.end + 'T00:00:00').getTime();
  const xSpan = Math.max(1, endMs - startMs);
  const xFor = (iso) => padL + ((new Date(iso + 'T00:00:00').getTime() - startMs) / xSpan) * chartW;

  // Y scaling — tight padding so flat-ish series still register visually.
  const values = data.map(p => p.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const span = maxV - minV;
  const yPad = span > 0 ? span * 0.18 : Math.max(0.5, Math.abs(maxV) * 0.1) || 1;
  const yMin = minV - yPad;
  const yMax = maxV + yPad;
  const yFor = (v) => padT + (1 - (v - yMin) / (yMax - yMin)) * chartH;

  const tickCount = 4;
  const ticks = Array.from({ length: tickCount }, (_, i) => yMin + (i / (tickCount - 1)) * (yMax - yMin));

  const points = data.map(p => [xFor(p.date), yFor(p.value)]);
  const linePath = cardinalSplinePath(points, 0.35);

  let areaPath = '';
  if (points.length >= 2) {
    areaPath = linePath
      + ` L${points[points.length - 1][0].toFixed(2)},${(padT + chartH).toFixed(2)}`
      + ` L${points[0][0].toFixed(2)},${(padT + chartH).toFixed(2)} Z`;
  }

  const dividerX = xFor(range.start);
  const startLabel = fmtDateShort(priorRange.start);
  const endLabel = fmtDateShort(range.end);
  const dividerLabel = fmtDateShort(range.start);

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y-axis: gridlines + value labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line
              x1={padL} x2={W - padR} y1={yFor(t)} y2={yFor(t)}
              stroke="var(--border)" strokeWidth="1" opacity="0.5"
            />
            <text
              x={padL - 6} y={yFor(t)} textAnchor="end" dominantBaseline="middle"
              fontSize="10" fill="var(--text-muted)" fontFamily="inherit"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {yFormat(t)}
            </text>
          </g>
        ))}

        {/* Period divider — vertical dashed at range.start */}
        {dividerX > padL && dividerX < W - padR && (
          <line x1={dividerX} x2={dividerX} y1={padT} y2={padT + chartH}
            stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="2 3" opacity="0.6" />
        )}

        {/* Area fill (gradient) */}
        {points.length >= 2 && areaPath && (
          <path d={areaPath} fill={`url(#${gradientId})`} />
        )}
        {/* Line stroke */}
        {points.length >= 2 && (
          <path d={linePath} fill="none" stroke="var(--accent-primary)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}

        {/* Data dots — cut-out effect via 2px stroke matching panel background.
            Hover overlay rect captures wider mouse target than the 4px dot.
            highlightDate gets a ringed treatment (larger r, accent ring) to
            anchor the operator on the invoice they're reviewing. */}
        {points.map(([x, y], i) => {
          const p = data[i];
          const isHighlight = highlightDate && p.date === highlightDate;
          return (
            <g key={i}>
              {isHighlight && (
                // Outer ring, drawn first so the inner dot lands on top.
                <circle
                  cx={x} cy={y} r="9"
                  fill="none"
                  stroke="var(--accent-primary)"
                  strokeWidth="1.5"
                  opacity="0.45"
                />
              )}
              <circle
                cx={x} cy={y} r={isHighlight ? 6 : 4}
                fill="var(--accent-primary)"
                stroke="var(--bg-card)" strokeWidth="2"
              />
              <rect
                x={x - 18} y={padT} width={36} height={chartH}
                fill="transparent"
                onMouseEnter={() => setHover({ x, y, point: p })}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer' }}
              >
                <title>{`${fmtDateShort(p.date)} \u00B7 ${yFormat(p.value)}`}</title>
              </rect>
            </g>
          );
        })}

        {/* Highlight pin — small "Current invoice" label above the
            highlighted dot. Drawn after the dots so it's not occluded.
            Suppressed if hover is on the same point (tooltip wins). */}
        {highlightDate && (() => {
          const idx = data.findIndex(p => p.date === highlightDate);
          if (idx < 0) return null;
          const [hx, hy] = points[idx];
          if (hover && Math.abs(hover.x - hx) < 1) return null;
          const labelW = Math.max(72, highlightLabel.length * 5.2 + 14);
          const labelH = 18;
          let lx = hx - labelW / 2;
          if (lx < padL) lx = padL;
          if (lx + labelW > W - padR) lx = W - padR - labelW;
          const ly = Math.max(padT, hy - labelH - 12);
          return (
            <g style={{ pointerEvents: 'none' }}>
              <rect
                x={lx} y={ly} width={labelW} height={labelH} rx="9"
                fill="var(--accent-primary)" opacity="0.92"
              />
              <text
                x={lx + labelW / 2} y={ly + 12}
                textAnchor="middle"
                fontSize="9.5" fontWeight="600"
                fill="white" fontFamily="inherit"
                style={{ letterSpacing: '0.04em' }}
              >
                {highlightLabel}
              </text>
            </g>
          );
        })()}

        {/* Pinned markers — pack change annotations etc.
            Position is **date-driven**, not data-driven: x is computed
            from the marker's date via xFor(); y attaches to the data
            point at that date when one exists, otherwise pins to the
            top of the chart with a thin connector down to the x-axis.
            This means a pack-change marker still shows even if the
            current invoice has no value on the active metric (e.g.
            $/lb is null on a counted-only item). */}
        {(markers || []).map((m, mi) => {
          if (!m || !m.date) return null;
          const mx = xFor(m.date);
          // Reject markers outside the rendered x-range. Defensive — a
          // malformed marker date shouldn't render off-canvas.
          if (mx < padL - 1 || mx > W - padR + 1) return null;

          const idx = data.findIndex(p => p.date === m.date);
          const dataPointY = idx >= 0 ? points[idx][1] : null;

          // Don't overlap the highlight pin if it's at the same date —
          // stack the marker below the dot instead of above.
          const stackBelow = highlightDate && m.date === highlightDate;

          const labelW = Math.max(64, (m.label || '').length * 5.2 + 12);
          const labelH = 16;
          let lx = mx - labelW / 2;
          if (lx < padL) lx = padL;
          if (lx + labelW > W - padR) lx = W - padR - labelW;

          // Y placement:
          //   data point present → 12px above (or 12px below if stacked
          //                        beneath the highlight pin).
          //   data point absent  → pin near the top of the chart, with a
          //                        thin dashed connector down to the
          //                        bottom of the chart so the operator
          //                        still sees "this happened on this date".
          const hasPoint = dataPointY != null;
          const ly = hasPoint
            ? (stackBelow
                ? Math.min(padT + chartH - labelH, dataPointY + 12)
                : Math.max(padT, dataPointY - labelH - 12))
            : padT + 4;

          const tone = m.tone || 'amber';
          const fill = tone === 'critical' ? 'var(--text-critical)'
            : tone === 'info' ? 'var(--text-info)'
            : 'var(--text-warning)';

          return (
            <g key={`marker-${mi}`} style={{ pointerEvents: 'none' }}>
              {/* Connector — small line from the marker pill toward the
                  data point (or the bottom of the chart if no point). */}
              <line
                x1={mx} x2={mx}
                y1={hasPoint ? (stackBelow ? dataPointY + 4 : dataPointY - 4) : ly + labelH}
                y2={hasPoint ? (stackBelow ? ly : ly + labelH) : padT + chartH}
                stroke={fill} strokeWidth="1"
                strokeDasharray={hasPoint ? undefined : '2 3'}
                opacity={hasPoint ? 0.8 : 0.55}
              />
              <rect
                x={lx} y={ly} width={labelW} height={labelH} rx="8"
                fill={fill} opacity="0.92"
              />
              <text
                x={lx + labelW / 2} y={ly + 11}
                textAnchor="middle"
                fontSize="9" fontWeight="600"
                fill="white" fontFamily="inherit"
                style={{ letterSpacing: '0.03em' }}
              >
                {m.label}
              </text>
            </g>
          );
        })}

        {/* Hover tooltip — multi-line when tooltipFormat is provided.
            Default behavior: date on top + yFormat(value) below. Custom
            callers (e.g. drill-in Volume tab) return ["Apr 7", "12 cases",
            "120 lb"] etc.; tooltip width + height grow to fit. */}
        {hover && (() => {
          const p = hover.point;
          const customLines = typeof tooltipFormat === 'function'
            ? (tooltipFormat(p) || []).filter(Boolean)
            : null;
          const lines = customLines && customLines.length > 0
            ? customLines
            : [fmtDateShort(p.date), yFormat(p.value)];
          // Approx width per char at 11px ~5.6, plus 16px horiz padding.
          // Min 100 to keep small tooltips from looking pinched.
          const longestLen = lines.reduce((m, s) => Math.max(m, String(s).length), 0);
          const tipW = Math.max(100, Math.min(220, longestLen * 5.8 + 16));
          const lineH = 14;
          const topPad = 8;
          const botPad = 8;
          const tipH = topPad + lineH * lines.length + botPad - 4;
          let tx = hover.x - tipW / 2;
          if (tx < padL) tx = padL;
          if (tx + tipW > W - padR) tx = W - padR - tipW;
          const ty = Math.max(padT, hover.y - tipH - 8);
          return (
            <g style={{ pointerEvents: 'none' }}>
              <line x1={hover.x} x2={hover.x} y1={padT} y2={padT + chartH}
                stroke="var(--accent-primary)" strokeWidth="1" opacity="0.4" strokeDasharray="2 2" />
              <g transform={`translate(${tx}, ${ty})`}>
                <rect width={tipW} height={tipH} rx="6"
                  fill="var(--bg-elevated)" stroke="var(--border)" strokeWidth="1" />
                {lines.map((line, i) => {
                  const isFirst = i === 0;
                  return (
                    <text
                      key={i}
                      x="8"
                      y={topPad + 6 + i * lineH}
                      fontSize={isFirst ? 10 : 11}
                      fontWeight={isFirst ? 400 : 600}
                      fill={isFirst ? 'var(--text-muted)' : 'var(--text-primary)'}
                      fontFamily="inherit"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {line}
                    </text>
                  );
                })}
              </g>
            </g>
          );
        })()}
      </svg>

      {/* X-axis date labels — HTML so truncation logic respects the panel width */}
      <div className="flex justify-between mt-1.5 px-1" style={{ fontSize: 10, color: 'var(--text-muted)' }}>
        <span>{startLabel}</span>
        {dividerX > padL + 60 && dividerX < W - padR - 60 && (
          <span style={{ color: 'var(--text-secondary)' }}>{`${dividerLabel} \u2014 period start`}</span>
        )}
        <span>{endLabel}</span>
      </div>
    </div>
  );
}
